import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useState } from 'react';

import { withFieldGroup } from '@/components/Form';
import { cn } from '@/lib/utils';

import RuleBuilderAttribute from './RuleBuilderAttribute';
import RuleBuilderMultiConditionHeader from './RuleBuilderMultiConditionHeader';
import RuleBuilderRemoveButton from './RuleBuilderRemoveButton';
import RuleBuilderText from './RuleBuilderText';
import RuleEntityAttributeDropdown from './RuleEntityAttributeDropdown';
import type {
  AndConditionPath,
  AttributePath,
  ConditionFieldValues,
  RuleBuilderAttributeType,
  RuleBuilderObjectType,
  RuleEntityType,
} from './types';
import { useAudienceRuleForm } from './types';

const buildAttributePath = (basePath: AndConditionPath, attrIndex: number): AttributePath =>
  `${basePath}.attributes[${attrIndex}]`;

const RuleBuilderMultiCondition = withFieldGroup({
  defaultValues: {
    attributes: [],
  } as ConditionFieldValues,
  props: {} as {
    basePath: AndConditionPath;
    ruleEntity: RuleEntityType;
    object: RuleBuilderObjectType;
    existingAttributes: Partial<RuleBuilderAttributeType>[];
    disabled?: boolean;
    onConditionRemove: () => void;
  },
  render: function RuleBuilderMultiConditionRender({
    group,
    basePath,
    ruleEntity,
    object,
    existingAttributes,
    disabled = false,
    onConditionRemove,
  }) {
    const form = useAudienceRuleForm();
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setFocused] = useState(false);

    const previouslyUsedAttributeFields = existingAttributes
      .map((attr) => attr.field)
      .filter((field) => field != null);

    const isAllAttributesUsed =
      previouslyUsedAttributeFields.length >= ruleEntity.attributes.length;

    return (
      <div
        className={cn(
          'relative border-l-3 border-gray-500 pl-4',
          'flex w-full flex-col items-start justify-center gap-6',
          !isAllAttributesUsed && 'pb-1',
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      >
        <RuleBuilderMultiConditionHeader
          basePath={basePath}
          ruleEntity={ruleEntity}
          object={object}
          disabled={disabled}
          onConditionRemove={onConditionRemove}
        />
        <group.Field name="attributes" mode="array">
          {(arrayField) => (
            <>
              <div className="flex w-full flex-col items-start justify-start gap-6">
                {arrayField.state.value.map((attrValue, attrIndex) => {
                  const attribute = ruleEntity.attributes.find((a) => a.id === attrValue.field);
                  if (!attribute) return null;

                  return (
                    <div
                      key={attrIndex}
                      className={cn(
                        'flex w-full items-start justify-start gap-6',
                        'max-lg:row-gap-1.5 max-lg:gap-3',
                      )}
                      data-testid={`${attribute.id}-attribute-condition`}
                    >
                      <div className={cn('flex w-17 shrink-0 justify-end', 'max-lg:w-4')}>
                        <RuleBuilderText variant="body2" italic>
                          {attrIndex > 0 ? <Trans>and</Trans> : <Trans>where</Trans>}
                        </RuleBuilderText>
                      </div>
                      <RuleBuilderAttribute
                        form={form}
                        fields={buildAttributePath(basePath, attrIndex)}
                        attribute={attribute}
                        disabled={disabled}
                      />
                      <RuleBuilderRemoveButton
                        title={<Trans>Remove condition</Trans>}
                        aria-label={t`Remove condition`}
                        disabled={disabled}
                        style={{ visibility: attribute.isRequired ? 'hidden' : 'visible' }}
                        onClick={() => arrayField.removeValue(attrIndex)}
                      />
                    </div>
                  );
                })}
              </div>
              {!isAllAttributesUsed && (
                <div
                  className={cn('flex w-17 shrink-0 translate-x-1.5 justify-end', 'max-lg:w-10')}
                >
                  <RuleEntityAttributeDropdown
                    ruleEntity={ruleEntity}
                    previouslyUsed={previouslyUsedAttributeFields}
                    isHidden={!isHovered && !isFocused}
                    disabled={disabled}
                    onSelect={(attribute) =>
                      arrayField.pushValue({
                        field: attribute.id,
                        operator: 'eq',
                        value: undefined,
                        type: attribute.type,
                        display: attribute.display,
                      })
                    }
                  />
                </div>
              )}
            </>
          )}
        </group.Field>
      </div>
    );
  },
});

export default RuleBuilderMultiCondition;
